import React, { useState, useEffect } from 'react';
import { 
  ArchiveBoxIcon, 
  PlusIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import apiClient from '../utils/api';

interface InventoryItem {
  id: string;
  name: string;
  category: 'feed' | 'medicine' | 'seed_fish' | 'equipment';
  unit: string;
  farm_id: string;
  current_stock: number;
  unit_price?: number;
  supplier?: string;
  low_stock_threshold?: number;
  created_at: string;
}

interface Farm {
  id: string;
  name: string;
  farm_type: string;
}

const Inventory: React.FC = () => {
  const { user } = useAuth();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState<string>('');
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateItem, setShowCreateItem] = useState(false);

  useEffect(() => {
    loadFarms();
  }, []);

  useEffect(() => {
    if (selectedFarmId) {
      loadInventory(selectedFarmId);
    }
  }, [selectedFarmId]);

  const loadFarms = async () => {
    try {
      const response = await apiClient.get('/farms');
      setFarms(response.data);
      if (response.data.length > 0) {
        setSelectedFarmId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading farms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInventory = async (farmId: string) => {
    try {
      const response = await apiClient.get(`/farms/${farmId}/inventory`);
      setInventory(response.data);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'feed':
        return '🌾';
      case 'medicine':
        return '💊';
      case 'seed_fish':
        return '🐠';
      case 'equipment':
        return '🔧';
      default:
        return '📦';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'feed':
        return 'bg-green-100 text-green-800';
      case 'medicine':
        return 'bg-red-100 text-red-800';
      case 'seed_fish':
        return 'bg-blue-100 text-blue-800';
      case 'equipment':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isLowStock = (item: InventoryItem) => {
    return item.low_stock_threshold && item.current_stock <= item.low_stock_threshold;
  };

  if (isLoading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
        {user?.role !== 'user' && selectedFarmId && (
          <button
            onClick={() => setShowCreateItem(true)}
            className="btn-touch bg-primary-600 text-white p-3 rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Farm Selector */}
      {farms.length > 0 && (
        <div className="card p-4">
          <label className="form-label">Select Farm</label>
          <select
            value={selectedFarmId}
            onChange={(e) => setSelectedFarmId(e.target.value)}
            className="form-input"
          >
            {farms.map((farm) => (
              <option key={farm.id} value={farm.id}>
                {farm.name} ({farm.farm_type} farm)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Inventory List */}
      {selectedFarmId ? (
        inventory.length > 0 ? (
          <div className="space-y-4">
            {inventory.map((item) => (
              <div key={item.id} className="card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <span className="text-3xl">{getCategoryIcon(item.category)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        {isLowStock(item) && (
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`}>
                          {item.category.replace('_', ' ').toUpperCase()}
                        </span>
                        {isLowStock(item) && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            LOW STOCK
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>Stock: {item.current_stock} {item.unit}</div>
                        {item.unit_price && (
                          <div>Price: ₹{item.unit_price}/{item.unit}</div>
                        )}
                        {item.supplier && (
                          <div className="col-span-2">Supplier: {item.supplier}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card p-8 text-center">
            <ArchiveBoxIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No inventory items</h3>
            <p className="text-gray-500 mb-6">
              Add inventory items to track your feed, medicine, and equipment.
            </p>
            {user?.role !== 'user' && (
              <button
                onClick={() => setShowCreateItem(true)}
                className="btn-touch bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 font-medium"
              >
                Add First Item
              </button>
            )}
          </div>
        )
      ) : (
        <div className="card p-8 text-center">
          <ArchiveBoxIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No farms available</h3>
          <p className="text-gray-500">
            Create a farm first to manage inventory items.
          </p>
        </div>
      )}

      {/* Create Item Modal */}
      {showCreateItem && selectedFarmId && (
        <CreateInventoryModal
          farmId={selectedFarmId}
          onClose={() => setShowCreateItem(false)}
          onSuccess={() => {
            setShowCreateItem(false);
            loadInventory(selectedFarmId);
          }}
        />
      )}
    </div>
  );
};

// Create Inventory Item Modal
const CreateInventoryModal: React.FC<{
  farmId: string;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ farmId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'feed' as 'feed' | 'medicine' | 'seed_fish' | 'equipment',
    unit: '',
    current_stock: '',
    unit_price: '',
    supplier: '',
    low_stock_threshold: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.unit.trim()) return;

    setIsLoading(true);
    try {
      const itemData = {
        ...formData,
        farm_id: farmId,
        current_stock: parseFloat(formData.current_stock) || 0,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : undefined,
        low_stock_threshold: formData.low_stock_threshold ? parseFloat(formData.low_stock_threshold) : undefined,
      };

      await apiClient.post('/inventory', itemData);
      onSuccess();
    } catch (error) {
      console.error('Error creating inventory item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4">
      <div className="card w-full max-w-md bg-white rounded-t-xl sm:rounded-xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Add Inventory Item</h2>
            <button
              type="button"
              onClick={onClose}
              className="btn-touch text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div>
            <label className="form-label">Item Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter item name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="form-label">Category *</label>
            <select
              className="form-input"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
            >
              <option value="feed">Feed</option>
              <option value="medicine">Medicine</option>
              <option value="seed_fish">Seed Fish</option>
              <option value="equipment">Equipment</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Unit *</label>
              <input
                type="text"
                className="form-input"
                placeholder="kg, liters, pieces"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="form-label">Current Stock</label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                placeholder="0"
                value={formData.current_stock}
                onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Unit Price (₹)</label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                placeholder="0.00"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
              />
            </div>
            <div>
              <label className="form-label">Low Stock Alert</label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                placeholder="0"
                value={formData.low_stock_threshold}
                onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="form-label">Supplier</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter supplier name"
              value={formData.supplier}
              onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-touch py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.name.trim() || !formData.unit.trim()}
              className="flex-1 btn-touch py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Inventory;